import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Alert as $Alert,
  AutoComplete as $AutoComplete,
  Button as $Button,
  Breadcrumb as $Breadcrumb,
  Card as $Card,
  Checkbox as $Checkbox,
  DatePicker as $DatePicker,
  Dropdown as $Dropdown,
  DatePickerProps,
  Form as $Form,
  Input as $Input,
  InputProps,
  InputNumber as $InputNumber,
  Menu as $Menu,
  Modal as $Modal,
  Popover as $Popover,
  Radio as $Radio,
  RadioProps,
  Select as $Select,
  SelectProps as AntdSelectProps,
  Switch as $Switch,
  Table as $Table,
  TimePicker as $TimePicker,
  Tooltip as $Tooltip,
  Tabs as $Tabs,
  TreeSelect as $TreeSelect,
  Divider as $Divider,
  Typography as $Typography,
} from 'antd';
import type { DefaultOptionType } from 'antd/es/select';

import theme from '../../../theme';

/* ============ UI ============ */

export const PrimaryBlueButtonStyles = css`
  &,
  &.popup-btn {
    color: ${theme.colorsWhite};
    background-color: ${theme.colorsPrimaryBlue};
    border-color: ${theme.colorsPrimaryBlue};

    &:hover,
    &:focus {
      color: ${theme.colorsWhite};
      background-color: ${theme.colorsPrimaryBlue};
      border-color: ${theme.colorsPrimaryBlue};
    }

    &[disabled],
    &.disabled {
      background-color: ${theme.colorsDisabledGreyButtons};
      border-color: ${theme.colorsDisabledGreyButtons};
      color: ${theme.colorsWhite};
      &:hover,
      &:focus {
        background-color: ${theme.colorsDisabledGreyButtons};
        border-color: ${theme.colorsDisabledGreyButtons};
        color: ${theme.colorsWhite};
      }
    }
  }

  &.disabled {
    background-color: ${theme.colorsDisabledGreyButtons};
    border-color: ${theme.colorsDisabledGreyButtons};
    color: ${theme.colorsWhite};

    &:hover,
    &:focus {
      background-color: ${theme.colorsDisabledGreyButtons};
      border-color: ${theme.colorsDisabledGreyButtons};
      color: ${theme.colorsWhite};
    }
  }
`;

export const ButtonStyles = css`
  height: auto;
  font-size: ${theme.componentFontSize};
  line-height: 1.5;
  font-weight: bold;
  padding: 10px 20px;
  border-radius: 4px;
  color: ${theme.colorsPrimaryBlue};
  border-color: ${theme.colorsPrimaryBlue};
  display: inline-block;
  &:hover,
  &:focus {
    &:not(:disabled) {
      color: ${theme.colorsPrimaryBlue};
      border-color: ${theme.colorsPrimaryBlue};
    }
  }

  > .anticon + span,
  > span + .anticon,
  > .MuiSvgIcon-root + span,
  > span + .MuiSvgIcon-root {
    margin-left: 20px;
  }

  > .MuiSvgIcon-root {
    vertical-align: bottom;
  }

  &.ant-btn-primary-blue {
    ${PrimaryBlueButtonStyles}
  }

  &.ant-btn-secondary-bright-blue {
    color: ${theme.colorsWhite};
    background-color: ${theme.colorsSecondaryBlueAlt};
    border-color: ${theme.colorsSecondaryBlueAlt};

    &:hover,
    &:focus {
      color: ${theme.colorsWhite};
      background-color: ${theme.colorsSecondaryBlueAlt};
      border-color: ${theme.colorsSecondaryBlueAlt};
    }
  }

  &.ant-btn-secondary-light-blue {
    color: ${theme.colorsWhite};
    background-color: #7eb5e0;
    border-color: #7eb5e0;

    &:hover,
    &:focus {
      color: ${theme.colorsWhite};
      background-color: #7eb5e0;
      border-color: #7eb5e0;
    }
  }

  &.ant-btn-primary {
    color: ${theme.colorsPrimaryBlue};
    border-color: ${theme.colorsPrimaryBlue};
    background-color: ${theme.colorsWhite};
    border-width: ${theme.componentBorderWidth};

    &:hover,
    &:focus {
      color: ${theme.colorsPrimaryBlue};
      border-color: ${theme.colorsPrimaryBlue};
      background-color: ${theme.colorsWhite};
      border-width: ${theme.componentBorderWidth};
    }

    &[disabled] {
      background-color: ${theme.colorsWhite};
      border-color: ${theme.colorsDisabledGreyButtons};
      color: ${theme.colorsDisabledGreyText};
      &:hover,
      &:focus {
        background-color: ${theme.colorsWhite};
        border-color: ${theme.colorsDisabledGreyButtons};
        color: ${theme.colorsDisabledGreyText};
      }
    }
  }

  &.ant-btn-secondary {
    color: ${theme.colorsPrimaryBlue};
    border: none;
    box-shadow: none;

    > span {
      text-decoration: underline;
    }

    &:hover,
    &:focus {
      color: ${theme.colorsPrimaryBlue};
      border: none;
      box-shadow: none;
      > span {
        text-decoration: underline;
      }
    }

    &[disabled] {
      background-color: transparent;
      border: none;
      color: ${theme.colorsDisabledGreyText};
      &:hover,
      &:focus {
        background-color: transparent;
        border: none;
        color: ${theme.colorsDisabledGreyText};
      }
    }
  }

  &.ant-btn-link {
    color: ${theme.colorsSecondaryBlue};
    font-weight: normal;
    border: none;
    box-shadow: none;
    padding: 0;

    &:hover {
      color: ${theme.colorsSecondaryBlue};
      font-weight: bold;
      border: none;
      box-shadow: none;
    }

    &:focus {
      color: ${theme.colorsSecondaryBlue};
    }

    &[disabled] {
      background-color: transparent;
      border: none;
      color: ${theme.colorsDisabledGreyText};
      &:hover,
      &:focus {
        background-color: transparent;
        font-weight: normal;
        border: none;
        color: ${theme.colorsDisabledGreyText};
      }
    }

    & > span {
      display: inline;
      text-decoration: underline;
    }
  }

  &a.ant-btn {
    line-height: 1.499;
    padding-top: 10px !important;
  }

  .ant-btn-icon {
    margin-right: 10px;
  }
`;

/* ------------ BUTTON ------------ */
export const Button = styled($Button)`
  &.ant-btn {
    ${ButtonStyles}
  }
`;

/* ------------ TOOLTIP ------------ */

export const Tooltip = styled($Tooltip)`
  .ant-tooltip {
    .ant-tooltip-inner {
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.13);
      border: 1.5px solid ${theme.colorsSecondaryBlueAlt};
      background-color: ${theme.colorsWhite};
      padding: 13px;
      font-size: 14px;
      color: ${theme.colorsBlack};
      white-space: pre-wrap;
    }
    .ant-tooltip-arrow {
      display: none;
    }
  }
`;

/* ------------ SWITCH ------------ */
export const Switch = styled($Switch)`
  &.ant-switch.ant-switch-checked {
    &,
    &:hover {
      background-color: ${theme.colorsPrimaryBlue};
    }
  }
`;

/* ---------- DATEPICKER ---------- */
export const StyledDatePicker = styled($DatePicker)`
  &.ant-calendar-input-wrap {
    height: calc(${theme.componentHeight} + 2px);
    padding: 0;
  }

  &.ant-calendar-date-input-wrap {
    height: 100%;
    font-size: ${theme.componentFontSize};
  }

  &.ant-calendar-range-picker-separator {
    vertical-align: baseline;
  }

  &.ant-calendar-range .ant-calendar-input,
  .ant-calendar-range .ant-calendar-time-picker-input,
  .ant-calendar-input {
    height: 100%;
    padding: 4px 11px;
    color: ${theme.colorsBlack};

    &::placeholder {
      color: rgba(0, 0, 0, 0.5);
    }
  }

  &.ant-calendar-picker-input {
    height: ${theme.componentHeight};
  }

  &.ant-calendar-picker-container {
    color: ${theme.colorsBlack};
  }

  &.ant-calendar-picker,
  &.ant-picker {
    width: 100% !important;
    height: ${theme.componentHeight};
    &:hover {
      .ant-calendar-picker-input:not(.ant-input-disabled) {
        border-color: ${theme.colorsPrimaryBlue};
      }

      .ant-calendar-picker-clear {
        opacity: 0;
      }
    }
    &.ant-picker-range {
      width: 100%;
    }
  }

  &.has-error .ant-calendar-picker {
    &:hover {
      .ant-calendar-picker-input:not(.ant-input-disabled) {
        border-color: ${theme.colorsErrorState100};
      }
    }
  }

  &.ant-calendar-header {
    .ant-calendar-prev-century-btn,
    .ant-calendar-next-century-btn,
    .ant-calendar-prev-decade-btn,
    .ant-calendar-next-decade-btn,
    .ant-calendar-prev-month-btn,
    .ant-calendar-next-month-btn,
    .ant-calendar-prev-year-btn,
    .ant-calendar-next-year-btn {
      font-family: ${theme.fontFamily};
      font-weight: bold;

      &:before,
      &:after {
        width: 10px;
        height: 10px;
        border: 0 solid ${theme.colorsBlack};
        border-width: 2px 0 0 2px;
      }
    }

    .ant-calendar-prev-year-btn {
      padding-right: 0;
    }

    .ant-calendar-prev-month-btn {
      padding-left: 10px;
    }

    .ant-calendar-next-month-btn {
      padding-right: 10px;
    }

    .ant-calendar-next-year-btn {
      padding-left: 0;
    }

    .ant-calendar-century-select,
    .ant-calendar-decade-select,
    .ant-calendar-year-select,
    .ant-calendar-month-select {
      font-weight: bold;
    }
  }

  &.ant-calendar-column-header {
    .ant-calendar-column-header-inner {
      font-weight: bold;
    }
  }

  &.ant-calendar-date {
    &:hover {
      color: ${theme.colorsWhite};
      background-color: ${theme.colorsPrimaryBlue};
    }
  }

  &.ant-calendar-today .ant-calendar-date {
    color: ${theme.colorsPrimaryBlue};
    border-color: ${theme.colorsPrimaryBlue};
  }

  &.ant-calendar-selected-day .ant-calendar-date {
    color: ${theme.colorsWhite};
    font-weight: bold;
    background-color: ${theme.colorsPrimaryBlue};
  }

  &.ant-calendar-picker-icon {
    color: ${theme.colorsBlack};
  }
`;

type DatePickerType = React.ForwardRefExoticComponent<DatePickerProps & React.RefAttributes<HTMLInputElement>> & {
  RangePicker: typeof $DatePicker.RangePicker;
};

export const DatePicker = StyledDatePicker as unknown as DatePickerType;

/* ---------- RANGEPICKER ---------- */
export const StyledRangePicker = styled($DatePicker.RangePicker)`
  && {
    height: ${theme.componentHeight};
  }
`;

DatePicker.RangePicker = StyledRangePicker as unknown as DatePickerType['RangePicker'];

/* ---------- TIMEPICKER ---------- */
export const TimePicker = styled($TimePicker)`
  width: 100% !important;

  .ant-picker-input > input {
    height: ${theme.componentHeight};
    color: ${theme.colorsBlack};
    border-color: ${theme.colorsMediumGreyOutline};
    box-shadow: none;

    &:focus,
    &:hover {
      border-color: ${theme.colorsPrimaryBlue};
      border-width: ${theme.componentBorderWidth};
      border-right-width: ${theme.componentBorderWidth} !important;
      box-shadow: none;
    }
  }

  &.ant-picker-disabled {
    .ant-picker-input > input {
      border-color: ${theme.componentDisabledBorderColor};
      background-color: ${theme.componentDisabledBgColor};
      color: ${theme.colorsDisabledGreyText};

      &:hover {
        border-color: ${theme.componentDisabledBorderColor};
        background-color: ${theme.componentDisabledBgColor};
        border-width: 1px;
        border-right-width: 1px !important;
      }
    }
  }

  .ant-picker-panel-container {
    .ant-picker-panel {
      .ant-picker-time-panel-column {
        > li {
          height: ${theme.componentHeight};
        }
      }
    }
  }
`;

/* ------------ RADIO ------------- */
const StyledRadio = styled($Radio)`
  &.ant-radio-wrapper {
    display: block;
    font-size: ${theme.componentFontSize};
    line-height: 2;
    color: ${theme.colorsBlack};

    .ant-radio-inner {
      width: 20px;
      height: 20px;
      border: 1px solid ${theme.colorsPrimaryBlue};
      &:after {
        background-color: ${theme.colorsPrimaryBlue};
      }
    }
  }

  &.ant-radio-checked {
    .ant-radio-inner {
      border-color: ${theme.colorPrimaryBlue};
    }
  }

  &.ant-radio-disabled {
    .ant-radio-inner {
      border-color: ${theme.colorsHr};
    }
  }

  &.ant-radio-wrapper:hover .ant-radio,
  &.ant-radio:hover .ant-radio-inner,
  .ant-radio-input:focus + .ant-radio-inner {
    border-color: ${theme.colorPrimaryBlue};
  }

  span.ant-radio + * {
    white-space: pre-wrap;
  }

  &.ant-radio-wrapper-checked {
    .ant-radio-checked {
      .ant-radio-inner {
        background-color: ${theme.colorsWhite};

        :after {
          transform: scale(0.5);
        }
      }
    }
  }

  .ant-radio {
    top: 0.2rem;
  }
`;

const StyledRadioGroup = styled($Radio.Group)`
  &.ant-radio-group-solid {
    .ant-radio-button-wrapper {
      height: auto;
      font-size: 16px;
      padding: 4px 16px;

      &.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
        background-color: ${theme.colorsSecondaryBlue};
        border-color: ${theme.colorsSecondaryBlue};

        &::before {
          background-color: ${theme.colorsSecondaryBlue};
        }
      }

      &:not(.ant-radio-button-wrapper-checked):not(.ant-radio-button-wrapper-disabled) {
        &:hover {
          color: ${theme.colorsPrimaryBlue};
        }
      }
    }
  }
`;

type RadioType = React.ForwardRefExoticComponent<RadioProps & React.RefAttributes<HTMLInputElement>> & {
  Group: typeof $Radio.Group;
  Button: typeof $Radio.Button;
};

export const Radio = StyledRadio as unknown as RadioType;

Radio.Group = StyledRadioGroup as unknown as typeof $Radio.Group;
Radio.Button = $Radio.Button;

/* ---------- CHECKBOX ------------ */
const StyledCheckbox = styled($Checkbox)`
  font-size: ${theme.componentFontSize};
  &.ant-checkbox-wrapper {
    .ant-checkbox-inner {
      border-radius: 2px;
    }

    &-disabled {
      &.ant-checkbox-wrapper-checked {
        &.ant-checkbox-wrapper-disabled {
          .ant-checkbox-inner:after {
            border-color: ${theme.colorsWhite};
          }
        }
      }
    }
  }

  &.ant-checkbox-wrapper:not(.ant-checkbox-wrapper-disabled):hover .ant-checkbox-checked:not(.ant-checkbox-disabled) {
    &:after {
      border-color: ${theme.colorsPrimaryBlue};
    }

    .ant-checkbox-inner {
      border-color: ${theme.colorsPrimaryBlue};
      background-color: ${theme.colorsPrimaryBlue};
    }
  }

  &.ant-checkbox-wrapper,
  .ant-checkbox-wrapper:hover {
    font-size: ${theme.componentFontSize};
    color: ${theme.colorsBlack};

    .ant-checkbox,
    .ant-checkbox:hover,
    .ant-checkbox-checked:hover {
      padding: 0;
      top: -2px;

      & .ant-checkbox-inner,
      .ant-checkbox-inner:hover {
        width: 20px;
        height: 20px;
        border-radius: 2px;
        border-color: ${theme.colorsPrimaryBlue};
      }
    }

    .ant-checkbox-checked.ant-wave-target,
    .ant-checkbox-checked:hover {
      background-color: ${theme.colorsPrimaryBlue};
      border-color: ${theme.colorsPrimaryBlue};

      .ant-checkbox-inner,
      .ant-checkbox-inner:hover {
        border-color: ${theme.colorsPrimaryBlue};
        background-color: ${theme.colorsPrimaryBlue};
      }
    }

    .ant-checkbox-checked {
      .ant-checkbox-inner:hover {
        border-color: ${theme.colorsPrimaryBlue};
        background-color: ${theme.colorsPrimaryBlue};
      }
    }
  }
`;

const StyledCheckboxGroup = styled($Checkbox.Group)`
  &.ant-checkbox-group {
    display: flex;
    flex-direction: column;
  }
`;

export const Checkbox = StyledCheckbox as unknown as typeof $Checkbox;

Checkbox.Group = StyledCheckboxGroup as unknown as typeof $Checkbox.Group;

/* -------- INPUTNUMBER ---------- */

export const InputNumber = styled($InputNumber)`
  &.ant-input-number {
    height: ${theme.componentHeight};
    width: 100%;
    font-size: ${theme.componentFontSize};
    color: ${theme.colorsBlack};
    border-color: ${theme.colorsDarkGreyOutline};
    box-shadow: none;
    border-radius: 0;

    &:focus,
    &:hover {
      border-color: ${theme.colorsPrimaryBlue};
      border-width: ${theme.componentBorderWidth};
      border-right-width: ${theme.componentBorderWidth} !important;
      box-shadow: none;

      .ant-input-number-handler-wrap {
        opacity: 0;
      }
    }

    &.ant-input-number-disabled {
      border-color: ${theme.componentDisabledBorderColor};
      background-color: ${theme.componentDisabledBgColor};
      border-width: 1px;

      &:focus,
      &:hover {
        border-color: ${theme.componentDisabledBorderColor};
        background-color: ${theme.componentDisabledBgColor};
        border-width: 1px;
        border-right-width: 1px !important;
      }
    }

    .ant-input-number-input {
      font-family: ${theme.fontFamily};
      height: ${theme.componentHeight};

      &:focus,
      &:hover {
        height: calc(${theme.componentHeight} - ${theme.componentBorderWidth});
      }
    }
  }

  &.ant-input-number-input::placeholder {
    color: rgba(0, 0, 0, 0.5);
  }

  &.ant-input-number__placeholder {
    color: rgba(0, 0, 0, 0.5);
  }

  &.has-error {
    .ant-input-number {
      background-color: ${theme.colorsErrorStateFill};
      border-color: ${theme.colorsErrorState100};

      &:focus,
      &:hover {
        background-color: ${theme.colorsErrorStateFill};
        border-color: ${theme.colorsErrorState100};
        border-right-width: ${theme.componentBorderWidth} !important;
        box-shadow: none;
      }
    }
  }
`;
/* ----------- INPUT ----------- */
const StyledInput = styled($Input)`
  font-size: ${theme.componentFontSize};

  &.ant-input-affix-wrapper {
    height: ${theme.componentHeight};

    &:hover {
      .ant-input:not(.ant-input-disabled) {
        border-color: ${theme.colorsPrimaryBlue};
        border-right-width: ${theme.componentBorderWidth} !important;
      }
    }
  }
  &.ant-input {
    height: ${theme.componentHeight};
    font-size: ${theme.componentFontSize};
    color: ${theme.colorsBlack};
    border-color: ${theme.colorsMediumGreyOutline};
    box-shadow: none;

    &:focus,
    &:hover {
      border-color: ${theme.colorsPrimaryBlue};
      border-width: ${theme.componentBorderWidth};
      border-right-width: ${theme.componentBorderWidth} !important;
      box-shadow: none;
    }

    &[disabled] {
      border-color: ${theme.componentDisabledBorderColor};
      background-color: ${theme.componentDisabledBgColor};
      color: ${theme.colorsDisabledGreyText};
      border-width: 1px;

      &:hover {
        border-color: ${theme.componentDisabledBorderColor};
        background-color: ${theme.componentDisabledBgColor};
        border-width: 1px;
        border-right-width: 1px !important;
      }
    }
  }

  &.ant-input::placeholder {
    color: rgba(0, 0, 0, 0.5);
    font-size: ${theme.componentFontSize};
  }

  &.ant-input__placeholder {
    color: rgba(0, 0, 0, 0.5);
    font-size: ${theme.componentFontSize};
  }

  &.has-error {
    .ant-input-affix-wrapper {
      .ant-input {
        &:focus,
        &:hover {
          border-right-width: ${theme.componentBorderWidth} !important;
          box-shadow: none;
        }
      }
    }
    .ant-input {
      background-color: ${theme.colorsErrorStateFill};
      border-color: ${theme.colorsErrorState100};

      &:focus,
      &:hover {
        background-color: ${theme.colorsErrorStateFill};
        border-color: ${theme.colorsErrorState100};
        border-right-width: ${theme.componentBorderWidth} !important;
        box-shadow: none;
      }
    }
  }
`;
const StyledPassword = styled($Input.Password)`
  &.ant-input-affix-wrapper.ant-input-outlined.ant-input-password {
    height: ${theme.componentHeight};

    font-size: ${theme.componentFontSize};
    border-radius: 4px;
    padding: 4px 11px;

    font-size: ${theme.componentFontSize};
    color: ${theme.colorsBlack};
    border-color: ${theme.colorsMediumGreyOutline};
    box-shadow: none;

    &:focus,
    &:hover {
      border-color: ${theme.colorsPrimaryBlue};
      border-width: ${theme.componentBorderWidth} !important;
      box-shadow: none;
    }

    &[disabled] {
      border-color: ${theme.componentDisabledBorderColor};
      background-color: ${theme.componentDisabledBgColor};
      color: ${theme.colorsDisabledGreyText};
      border-width: 1px;

      &:hover {
        border-color: ${theme.componentDisabledBorderColor};
        background-color: ${theme.componentDisabledBgColor};
        border-width: 1px;
        border-right-width: 1px !important;
      }
    }
  }
`;

type InputType = React.ForwardRefExoticComponent<InputProps & React.RefAttributes<HTMLInputElement>> & {
  Group: typeof $Input.Group;
  Search: typeof $Input.Search;
  TextArea: typeof $Input.TextArea;
  Password: typeof $Input.Password;
  OTP: typeof $Input.OTP;
};

export const Input = StyledInput as unknown as InputType;

Input.Group = $Input.Group;
Input.Search = $Input.Search;
Input.TextArea = $Input.TextArea;
Input.Password = StyledPassword as unknown as typeof $Input.Password;
Input.OTP = $Input.OTP;

/* --------- FORMITEM ---------- */
const StyledFormItem = styled($Form.Item)`
  &.ant-form-item .ant-form-item-label {
    font-size: ${theme.componentFontSize};
    line-height: 1.5;
    padding: 0;
    text-align: start;

    > label {
      font-size: 16px;
      font-weight: bold;
      line-height: 1.5;
      height: 100%;

      &.ant-form-item-no-colon {
        &:after {
          display: none;
        }
      }
    }

    &.ant-form-item {
      .ant-input.ant-input-affix-wrapper.ant-input-password {
        height: ${theme.componentHeight};
      }
    }
  }

  .ant-input {
    border-radius: 4px;
  }

  .ant-form-item-control-input-content {
    min-height: ${theme.componentHeight};
  }

  .ant-form-item-row {
    display: block;
  }

  // normalize labels and px won't work for the visually impaired
  &.ant-label {
    line-height: 1.5;
    font-size: ${theme.componentFontSize};
    font-weight: bold;
    color: ${theme.colorsBlack};
  }

  &.ant-form-explain {
    margin-top: 12px;
  }
`;
export const Form = { ...$Form } as typeof $Form;
Form.Item = StyledFormItem as unknown as typeof $Form.Item;

export const ModalFooterWrapper = styled.div`
  position: relative;

  .ant-modal-content {
    padding: 0;
    border-radius: 0;

    .ant-modal-confirm-body-wrapper {
      .ant-modal-confirm-body {
        padding: 24px 24px 12px;
      }

      .ant-modal-confirm-title {
        font-size: 24px;
        line-height: 1.5rem;
      }

      .ant-modal-confirm-content {
        color: ${theme.colorsBlack};
        font-size: 18px;
      }

      .ant-modal-confirm-paragraph {
        max-width: 100%;
        row-gap: 0;
      }

      .ant-modal-confirm-btns {
        border-top: 1px solid ${theme.colorsHr};
        padding: 16px 24px;
        margin-top: 0;

        .ok-btn,
        .cancel-btn {
          min-width: 120px;
        }

        @media (max-width: 1199px) {
          .ok-btn,
          .cancel-btn {
            min-width: 100px;
            padding: 7px 18px;
            font-size: 14px;
            line-height: 21px;
          }
        }
      }
    }
  }

  @media (max-width: 767px) {
    max-width: calc(100vw - 16px);
  }

  @media (max-width: 1199px) {
    min-width: auto;
    position: inherit;

    .ant-modal-content {
      .ant-modal-confirm-title {
        font-size: 18px;
        line-height: 28px;
        padding: 16px 50px 12px 16px;
      }

      .ant-modal-confirm-content {
        font-size: 16px;
        padding: 16px;
      }
    }
  }

  * {
    ${Button?.__emotion_styles?.join('')};
  }
`;

/* ---------- POPOVER ---------- */
const StyledPopover = styled($Popover)`
  &.ant-popover-inner {
    border: none;
    max-width: 214px;
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
    background-color: ${theme.colorsWhite};
    font-size: 14px;
    padding: 12px 16px;
    word-wrap: break-word;
  }

  &.ant-custom-popover {
    &.ant-popover-arrow {
      visibility: hidden;
    }

    &.ant-popover-inner-content {
      padding: 0px;
    }

    &.ant-popover-inner {
      padding: 0px;
    }
  }
`;

export const Popover = StyledPopover as unknown as typeof $Popover;

/* ---------- ALERT ---------- */
export const Alert = styled($Alert)`
  &.ant-alert {
    border-radius: 2px;
    padding-top: 10px;
    padding-bottom: 10px;

    .ant-alert-message {
      color: ${theme.colorsBlack};
      font-size: 14px;
      line-height: 1.5;
      vertical-align: middle;
    }

    .ant-alert-icon {
      top: 8px;
      left: 8px;
    }

    .ant-alert-close-icon {
      top: 10px;

      .anticon-close {
        color: ${theme.colorsBlack};
      }
    }
  }
`;

/* ----------- SELECT ---------- */
const antSelectAntSelectLgAntSelectSelectionSingle = css`
  height: ${theme.componentHeight};
`;

const antSelectAntSelectLgAntSelectSelectionRendered = css`
  line-height: ${theme.componentHeight};
`;

const antSelectAntSelectSelectionPlaceholder = css`
  color: rgba(0, 0, 0, 0.5);
`;

const StyledSelect = styled($Select)`
  &.ant-select-selector {
    border-radius: 4px !important;
    height: ${theme.componentHeight};
  }

  &.ant-select-outlined {
    .ant-select-selector {
      height: ${theme.componentHeight};
    }
  }

  &.ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    height: ${theme.componentHeight};
    border: 1px solid ${theme.colorsMediumGreyOutline};
    font-size: ${theme.componentFontSize};
  }
  &.ant-select-selection {
    border: 1px solid ${theme.colorsMediumGreyOutline};
  }

  &.ant-select-disabled .ant-select-selection {
    border-color: ${theme.componentDisabledBorderColor};
    background-color: ${theme.componentDisabledBgColor};
    border-width: 1px;
    border-radius: 4px;
    color: ${theme.colorDisabledGreyText};
    height: ${theme.componentHeight};

    &:hover {
      border-color: ${theme.componentDisabledBorderColor};
      background-color: ${theme.componentDisabledBgColor};
      border-width: 1px;
      border-right-width: 1px !important;
      border-radius: 4px;
    }
  }

  &.ant-select {
    width: 100%;
    color: ${theme.colorBlack};
    height: ${theme.componentHeight};

    &.ant-select-lg {
      .ant-select-selection--single {
        ${antSelectAntSelectLgAntSelectSelectionSingle};
      }

      .ant-select-selection__rendered {
        ${antSelectAntSelectLgAntSelectSelectionRendered};
      }

      .ant-select-selection--multiple {
        .ant-select-selection__rendered {
          li {
            margin-top: 6px;
          }
        }

        .ant-select-selection__choice__content {
          padding-right: 5px;
        }
      }
    }

    &:not(.ant-select-disabled) {
      &.ant-select-focused {
        .ant-select-selection {
          border-color: ${theme.colorPrimaryBlue};
          border-width: ${theme.componentBorderWidth};
          border-right-width: ${theme.componentBorderWidth} !important;
          box-shadow: none;
        }
      }

      .ant-select-selection {
        &:hover,
        &:focus {
          border-color: ${theme.colorPrimaryBlue};
          border-width: ${theme.componentBorderWidth};
          border-right-width: ${theme.componentBorderWidth} !important;
          box-shadow: none;
        }
      }
    }

    .ant-select-selection__placeholder {
      ${antSelectAntSelectSelectionPlaceholder};
    }

    .ant-select-selection__rendered {
      margin-left: 18px;
      margin-right: 34px;
    }

    .ant-select-selection__clear {
      right: 18px;
    }

    .ant-select-arrow {
      color: ${theme.colorsBlack};
      right: 16px;
      width: 16px;
      height: 16px;
      margin-top: -8px;
      font-size: 14px;
    }
  }

  &.has-error {
    .ant-select {
      .ant-select-selection {
        background-color: ${theme.colorsErrorStateFill};
        border-color: ${theme.colorsErrorState100};

        &:hover,
        &:focus {
          background-color: ${theme.colorsErrorStateFill};
          border-color: ${theme.colorsErrorState100};
        }
      }
    }
  }

  &.ant-select-dropdown {
    .ant-select-dropdown-menu {
      padding: 0;
      margin-left: 0;

      .ant-select-dropdown-menu-item {
        padding: 10px 12px;
        font-size: ${theme.componentFontSize};
        color: ${theme.colorsBlack};
        margin-bottom: 0;

        &.ant-select-dropdown-menu-item-active:not(.ant-select-dropdown-menu-item-disabled),
        &:hover:not(.ant-select-dropdown-menu-item-disabled) {
          background-color: ${theme.colorsPrimaryBlue};
          color: ${theme.colorsWhite};

          &.ant-select-dropdown-menu-item-selected {
            .ant-select-selected-icon {
              color: ${theme.colorsWhite};
            }
          }
        }

        &:hover {
          &.ant-select-dropdown-menu-item-selected {
            .ant-select-selected-icon {
              color: ${theme.colorsWhite};
            }
          }

          .ant-select-selected-icon {
            color: ${theme.colorsPrimaryBlue};
          }
        }

        &.ant-select-dropdown-menu-item-selected {
          background-color: ${theme.colorsWhite};
          .ant-select-selected-icon {
            color: ${theme.colorsBlack};
          }
        }

        .ant-select-selected-icon {
          color: ${theme.colorsWhite};
          transition: none;
        }
      }
    }
  }
`;

interface ExtendedSelectProps<ValueType = any, OptionType extends DefaultOptionType = DefaultOptionType>
  extends AntdSelectProps<ValueType, OptionType> {
  name?: string;
}

export const Select = StyledSelect as unknown as React.FC<ExtendedSelectProps> & {
  Option: typeof $Select.Option;
  OptGroup: typeof $Select.OptGroup;
  displayName?: string;
};

Select.Option = $Select.Option;
Select.OptGroup = $Select.OptGroup;
Select.displayName = $Select.displayName;

/* -------- AUTOCOMPLETE ------- */
export const hasErrorAntInputHover = css`
  background-color: ${theme.colorsErrorStateFill};
  border-color: ${theme.colorsErrorState100};
  border-right-width: ${theme.componentBorderWidth}px !important;
  box-shadow: none;
`;

export const hasErrorAntInput = css`
  background-color: ${theme.colorsErrorStateFill};
  border-color: ${theme.colorsErrorState100};

  &:focus,
  &:hover {
    ${hasErrorAntInputHover};
  }
`;

export const hasErrorAntInputAffixWrapperAntInputHover = css`
  border-right-width: ${theme.componentBorderWidth}px !important;
  box-shadow: none;
`;

export const antInputDisabledHover = css`
  border-color: ${theme.componentDisabledBorderColor};
  background-color: ${theme.componentDisabledBgColor};
  border-width: 1px;
  border-right-width: 1px !important;
`;

export const antInputDisabled = css`
  border-color: ${theme.componentDisabledBorderColor};
  background-color: ${theme.componentDisabledBgColor};
  color: ${theme.colorsDisabledGreyText};
  border-width: 1px;

  &:hover {
    ${antInputDisabledHover};
  }
`;

export const antInputHover = css`
  border-color: ${theme.colorsPrimaryBlue};
  border-width: ${theme.componentBorderWidth}px;
  border-right-width: ${theme.componentBorderWidth}px !important;
  box-shadow: none;
`;

export const antInput = css`
  height: ${theme.componentHeight};
  font-size: ${theme.componentFontSize};
  color: ${theme.colorsBlack};
  border-color: ${theme.colorsMediumGreyOutline};
  box-shadow: none;

  &.ant-input-affix-wrapper {
    &.ant-input-outlined {
      color: ${theme.colorsBlack};
    }
  }

  &:focus,
  &:hover {
    ${antInputHover};
  }

  &[disabled] {
    ${antInputDisabled};
  }
`;

const StyledAutoComplete = styled($AutoComplete)`
  .ant-input-outlined {
    border-radius: 2px;
  }
  .ant-select-auto-complete {
    font-size: ${theme.componentFontSize};

    &.ant-select {
      .ant-input {
        ${antInput};
        &:focus,
        &:hover {
          ${antInputHover};
        }

        &[disabled] {
          ${antInputDisabled};
          background-color: transparent;

          &:hover {
            ${antInputDisabledHover};
            background-color: transparent;
          }
        }
      }

      .ant-select-selection--single {
        ${antSelectAntSelectLgAntSelectSelectionSingle};
      }

      .ant-select-selection__rendered {
        ${antSelectAntSelectLgAntSelectSelectionRendered};
        margin-left: 0;
        margin-right: 0;
      }

      .ant-select-selection__placeholder {
        ${antSelectAntSelectSelectionPlaceholder};
      }

      .ant-input-suffix {
        font-size: 14px;
      }

      &.ant-select-combobox .ant-select-arrow {
        display: none;
      }
    }
  }

  .has-error {
    .ant-input-affix-wrapper {
      .ant-input {
        &:focus,
        &:hover {
          ${hasErrorAntInputAffixWrapperAntInputHover};
        }
      }
    }

    .ant-select-auto-complete {
      &.ant-select {
        .ant-input {
          ${hasErrorAntInput};
          background-color: transparent;

          &:focus,
          &:hover {
            ${hasErrorAntInputHover};
            background-color: transparent;
          }
        }
      }
    }
  }
`;

export const AutoComplete = StyledAutoComplete as unknown as typeof $AutoComplete;
AutoComplete.Option = $AutoComplete.Option;
AutoComplete.displayName = $AutoComplete.displayName;

/* ---------- TABLE ---------- */
const StyledTable = styled($Table)`
  &.ant-table-wrapper {
    color: ${theme.colorsBlack};

    .ant-table {
      border-radius: 2px;
      font-size: 16px;

      .ant-table-header {
        border-radius: 2px 2px 0 0;

        table {
          border-radius: 2px 2px 0 0;
        }
      }

      .ant-table-thead {
        > tr {
          > th {
            color: ${theme.colorsDisabledGreyText};
            font-size: 14px;
            font-weight: bold;
            background-color: #f2f2f2;
            text-transform: uppercase;

            .ant-table-column-sorter {
              .ant-table-column-sorter-inner {
                .ant-table-column-sorter-up.on,
                .ant-table-column-sorter-down.on {
                  color: ${theme.colorsPrimaryBlue};
                }
              }
            }

            &:not(:last-of-type):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
                [colspan]
              )::before {
              background-color: rgba(0, 0, 0, 0.06);
            }
          }

          &:first-of-type {
            > :first-of-type {
              border-start-start-radius: 2px;
            }

            > :last-of-type {
              border-start-end-radius: 2px;
            }
          }
        }
      }

      .ant-table-tbody {
        > tr {
          background-color: ${theme.colorsWhite};

          > td {
            padding: 0;
            border-bottom: none;
            font-size: 14px;

            &:first-of-type {
              font-weight: bold;
            }
          }
        }
      }

      .ant-table-filter-dropdown {
        border: 1px solid ${theme.colorsHr};
        box-shadow: 0 2px 12px 0 ${theme.colorsMediumGrey};

        .ant-dropdown-menu-item,
        .ant-dropdown-menu-submenu-title {
          padding: 10px 18px;
        }

        .ant-dropdown-menu-item-selected,
        .ant-dropdown-menu-submenu-title-selected,
        .ant-dropdown-menu-item-selected > a,
        .ant-dropdown-menu-submenu-title-selected > a {
          color: ${theme.colorsWhite};
          background-color: ${theme.colorsPrimaryBlue};
        }

        .ant-table-filter-dropdown-btns {
          padding: 7px 18px;
          border-top: 1px solid ${theme.colorsHr};
        }

        .ant-table-filter-dropdown-link {
          height: auto;
          border-radius: 4px;
          color: ${theme.colorsBlack};

          &.confirm {
            float: right;
            font-size: 14px;
            font-weight: bold;
            color: ${theme.colorsWhite};
            background-color: ${theme.colorsPrimaryBlue};
            border-color: ${theme.colorsPrimaryBlue};
            padding: 8px 18px;
            margin-left: 10px;
          }

          &.clear {
            float: left;
            font-size: 16px;
            padding: 8px 0;
            margin-right: 10px;
          }
        }
      }
    }

    .ant-pagination {
      .ant-pagination-item {
        border-radius: 2px;
        border-color: #d9d9d9;

        &.ant-pagination-item-active {
          border-color: ${theme.colorsSecondaryBlueAlt};
          color: ${theme.colorsSecondaryBlueAlt};
        }

        :hover {
          border-color: ${theme.colorsSecondaryBlueAlt};
          background-color: ${theme.colorsWhite};

          a {
            color: ${theme.colorsSecondaryBlueAlt};
          }
        }
      }

      .ant-select-selector {
        cursor: pointer;
      }

      input {
        border-radius: 2px;
      }
    }

    .ant-checkbox-wrapper {
      font-size: 16px;
      line-height: 2;

      .ant-checkbox,
      .ant-checkbox:hover,
      .ant-checkbox-checked:hover {
        top: -2px;

        .ant-checkbox-inner:hover {
          border-color: ${theme.colorsPrimaryBlue};
        }
      }
    }
  }
`;

export const Table = StyledTable as unknown as typeof $Table;

/* ---------- TABS ---------- */
const StyledTabs = styled($Tabs)`
  &.ant-tabs {
    .ant-tabs-bar {
      .ant-tabs-extra-content {
        .ant-tabs-new-tab {
          &:hover {
            color: ${theme.colorsPrimaryBlue};
            border-color: ${theme.colorsPrimaryBlue};
          }
        }
      }

      .ant-tabs-tab-prev-icon-target,
      .ant-tabs-tab-next-icon-target {
        &:hover {
          color: ${theme.colorsPrimaryBlue};
        }
      }
    }

    .ant-tabs-bar {
      margin: 0;
      border-bottom: none;
    }

    .ant-tabs-nav-container {
      font-size: 16px;
      color: ${theme.colorsBlack};
    }

    .ant-tabs-nav {
      &.ant-tabs-tab {
        margin: 0 18px 0 0;
        padding: 5px 16px 15px 16px;
        &:hover {
          color: ${theme.colorsSecondaryBlue};
        }
      }

      &.ant-tabs-tab-active {
        color: ${theme.colorsSecondaryBlue} !important;
        border-color: ${theme.colorsSecondaryBlue};
      }
    }

    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
      color: ${theme.colorsSecondaryBlue} !important;
      text-shadow: none;
    }

    .ant-tabs-ink-bar {
      height: 5px;
      background-color: ${theme.colorsPrimaryBlue};
    }

    .ant-tabs-nav {
      margin-bottom: 0;
    }
  }
`;

export const Tabs = StyledTabs as unknown as typeof $Tabs;

/* ---------- TREE SELECT ---------- */
const StyledTreeSelect = styled($TreeSelect)`
  .ant-select-dropdown {
    border-radius: 2px;
  }

  .ant-select-tree-list {
    .ant-select-tree-title {
      font-size: 16px;
      color: ${theme.colorsBlack};
      &:hover {
        color: ${theme.colorsWhite};
      }
      &:focus {
        color: ${theme.colorsWhite};
      }
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  li {
    margin: 0px;
  }

  li .ant-select-tree-node-content-wrapper.ant-select-tree-node-selected {
    background-color: ${theme.colorsPrimaryBlue};
    padding-left: 12px;
    margin: 0px;
    > .ant-select-tree-title {
      color: ${theme.colorsWhite};
    }
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  li span.ant-select-tree-switcher.ant-select-tree-switcher-noop {
    width: 0px;
  }

  li span.ant-select-tree-switcher {
    width: 0px;
  }

  li .ant-select-tree-node-content-wrapper {
    width: 100%;
    padding: 10px 10px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    &:hover {
      background-color: ${theme.colorsPrimaryBlue};
      > .ant-select-tree-title {
        color: ${theme.colorsWhite};
      }
    }
  }

  .ant-select-tree-treenode-switcher-open .ant-select-tree-treenode-selected {
    &:hover {
      background-color: ${theme.colorsPrimaryBlue};
      color: ${theme.colorsWhite};
    }
    padding-left: 12px;
  }
`;

export const TreeSelect = StyledTreeSelect as unknown as typeof $TreeSelect;

/* ---------- DIVIDER ---------- */
const StyledDivider = styled($Divider)`
  &.ant-divider {
    background-color: ${theme.colorsHr};

    &.ant-divider-horizontal {
      &.ant-divider-with-text-left,
      &.ant-divider-with-text-center,
      &.ant-divider-with-text-right {
        font-size: 20px;
        font-weight: bold;
      }
    }
  }
`;

export const Divider = StyledDivider as unknown as typeof $Divider;

/* ---------- Modal ---------- */
const StyledModal = styled($Modal)`
  &.ant-modal {
    .ant-modal-confirm-error .ant-modal-confirm-body > .anticon {
      display: none;
    }

    .ant-modal-header {
      padding: 0;
      border-bottom: none;
      margin-bottom: 0;
    }

    .ant-modal-title {
      padding: 12px 40px 8px 24px;
      border-bottom: 1px solid ${theme.colorsHr};
      font-size: 24px;
      font-weight: bold;
      line-height: 40px;
      color: ${theme.colorsBlack};
    }

    .ant-modal-close + .ant-modal-body {
      padding: 18px 60px 18px 24px;
    }

    .manage-columns {
      .ant-modal-body {
        padding: 0px;
      }
    }

    .ant-modal-body {
      padding: 16px 24px;
      border-bottom: 1px solid ${theme.colorsHr};
      color: ${theme.colorsBlack};
      font-size: 18px;
      line-height: 24px;
      &:empty {
        display: none;
      }
    }

    .ant-modal-close {
      height: 54px;
      width: 54px;
      top: 0;
      right: 0;

      .ant-modal-close-x {
        display: contents;
        height: 32px;
      }

      &:hover {
        background-color: transparent;
      }
    }
  }

  @media (max-width: 767px) {
    &.ant-modal {
      margin: 8px auto;
      max-width: calc(100vw - 16px);
    }
  }

  @media (max-width: 1199px) {
    &.ant-modal {
      min-width: auto;

      .ant-modal-title {
        padding: 16px 48px 12px 16px;
        font-size: 18px;
        line-height: 28px;
      }
      .ant-modal-body {
        padding: 16px 16px;
        font-size: 16px;
      }
    }
  }

  .ant-modal-content {
    padding: 0;
    border-radius: 0;

    .ok-btn,
    .cancel-btn {
      min-width: 160px;
    }

    @media (max-width: 1199px) {
      .ok-btn,
      .cancel-btn {
        min-width: 100px;
        padding: 7px 18px;
        font-size: 14px;
        line-height: 21px;
      }
    }

    .ant-modal-close {
      color: ${theme.colorsBlack};
      .ant-modal-close-x {
        font-size: 32px;
      }
    }
  }

  .ant-modal-confirm .ant-modal-close {
    display: block;
  }

  .ant-modal-footer {
    padding: 16px 24px;
    border-top: 0;
    margin-top: 0;
    & .ant-btn + .ant-btn:not(.ant-dropdown-trigger) {
      margin-bottom: 0;
      margin-left: 8px;
    }
  }

  .ant-modal-confirm-body > .anticon + .ant-modal-confirm-title + .ant-modal-confirm-content {
    margin-left: 0;
  }

  .ant-modal-confirm {
    .ant-modal-body {
      padding: 0;
    }
    .ant-modal-confirm-body {
      .ant-modal-confirm-title {
        &: extend(: root .ant-modal-title);
      }
      .ant-modal-confirm-content {
        margin-top: 0;
        &: extend(: root .ant-modal-body);
        &:empty {
          display: none;
        }

        &:empty {
          display: none;
        }
      }
    }
    .ant-modal-confirm-btns {
      padding: 16px 24px;
      border-top: 0;
      margin-top: 0;
    }
  }
`;

export const Modal = StyledModal as unknown as typeof $Modal;

/* ---------- TYPOGRAPHY ---------- */
const StyledTypography = styled($Typography)`
  .ant-typography {
    margin-bottom: 0;

    &.ant-typography-disabled {
      color: ${theme.colorsDisabledGreyText};
    }
  }
`;

export const Typography = StyledTypography as unknown as typeof $Typography;
Typography.Text = $Typography.Text;
Typography.Title = $Typography.Title;
Typography.Link = $Typography.Link;
Typography.Paragraph = $Typography.Paragraph;

/* ---------- DROPDOWN ---------- */
const StyledDropdown = styled($Dropdown)`
  &.ant-dropdown-trigger {
    position: relative;
  }

  &.ant-dropdown-trigger > i.anticon.anticon-down,
  &.ant-dropdown-link > i.anticon.anticon-down {
    position: absolute;
    top: 50%;
    right: 16px;
    z-index: 1;
    width: 16px;
    height: 16px;
    margin-top: -8px;
    font-size: 14px;
  }

  .ant-dropdown-menu {
    .ant-dropdown-menu-item,
    .ant-dropdown-menu-submenu-title {
      padding: 10px 12px;
      font-size: 16px;
      color: ${theme.colorsBlack};

      &:hover,
      &:focus {
        background-color: ${theme.colorsPrimaryBlue};
        color: white;

        .ant-dropdown-menu-submenu-arrow-icon {
          color: white;
        }
      }

      &.ant-dropdown-menu-item-disabled,
      &.ant-dropdown-menu-submenu-title-disabled {
        color: rgba(0, 0, 0, 0.25);
        background-color: #ccc;
        border-color: #ccc;
      }
    }
  }
`;

export const Dropdown = StyledDropdown as unknown as typeof $Dropdown;

/* ---------- MENU ---------- */
const StyledMenu = styled($Menu)`
  && {
    border-inline-end: none !important;
    border-radius: 2px;
    font-size: 16px;
    font-weight: bold;
    .ant-dropdown-menu {
      padding-top: 4px;
      padding-bottom: 0;
      padding-left: 0;
      padding-right: 0;
      border-radius: 2px;
    }

    .ant-menu {
      padding-top: 4px;
      padding-bottom: 0;
      padding-left: 0;
      padding-right: 0;

      border-radius: 2px;
    }

    &.selectedLang,
    &.ant-menu-item-selected,
    &.ant-dropdown-menu-item-selected,
    &.ant-menu-submenu-selected,
    &.ant-dropdown-menu-submenu-title-selected {
      background-color: ${theme.colorsPrimaryBlue};
      color: ${theme.colorsWhite};

      &:hover,
      &:active,
      &:focus {
        background-color: ${theme.colorsPrimaryBlue};
        color: ${theme.colorsWhite};
      }

      & .ant-dropdown-menu-title-content {
        font-size: 16px;
      }
    }

    .ant-dropdown-menu-item,
    .ant-menu-item,
    .ant-dropdown-menu-submenu-title,
    .ant-menu-submenu-title {
      align-items: center;
      border-radius: 0;
      background-color: white;
      display: flex;
      font-size: 16px;
      margin: 4px 0 8px 0;

      .ant-dropdown-menu-title-content {
        font-size: 16px;
      }

      &:hover,
      &:active,
      &:focus {
        color: ${theme.colorsWhite};
        &:not(.ant-dropdown-menu-item-disabled) {
          background-color: ${theme.colorsPrimaryBlue};
        }
        &.ant-dropdown-menu-item-disabled {
          background-color: ${theme.colorsDisabledGreyButtons};
        }

        .ant-dropdown-menu-submenu-arrow-icon,
        .ant-menu-submenu-arrow {
          color: ${theme.colorsWhite};
        }
      }
    }
  }
  &.ant-dropdown-menu.ant-dropdown-menu-root.ant-dropdown-menu-vertical.ant-dropdown-menu-light {
    padding-top: 4px;
    padding-bottom: 4px;
    padding-left: 0;
    padding-right: 0;
    border-radius: 2px;
  }
`;

// Export a fully-featured Menu component
export const Menu = StyledMenu as unknown as typeof $Menu;

export const StyledMenuItem = styled($Menu.Item)`
  && {
    background-color: ${theme.colorsWhite};
    color: ${theme.colorsBlack};
    margin: 0px;
    border-radius: 0px;
    &.ant-menu-item:hover {
      background-color: ${theme.colorsPrimaryBlue};
      border-radius: 0px;
      color: ${theme.colorsWhite};
    }
    .ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-item,
    .ant-dropdown-menu-submenu .ant-dropdown-menu .ant-dropdown-menu-item,
    .ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-submenu-title,
    .ant-dropdown-menu-submenu .ant-dropdown-menu .ant-dropdown-menu-submenu-title {
      padding-top: 10px;
      padding-bottom: 10px;
      &.ant-dropdown-menu-item-disabled {
        color: ${theme.colorsDisabledGreyText};
      }
    }
  }
  .ant-menu-item {
    margin: 0px;
  }
`;

const StyledMenuSubMenu = styled($Menu.SubMenu)`
  && {
    margin-bottom: 1px;
  }
`;

Menu.Item = StyledMenuItem as unknown as typeof $Menu.Item;
Menu.SubMenu = StyledMenuSubMenu as unknown as typeof $Menu.SubMenu;
Menu.ItemGroup = $Menu.ItemGroup;
Menu.Divider = $Menu.Divider;

/* ---------- CARD ---------- */
const StyledCard = styled($Card)`
  &.ant-card {
    .ant-card-head .ant-card-head-wrapper .ant-card-head-title {
      padding-top: 16px;
      padding-bottom: 16px;
    }
    .ant-card-meta-title {
      font-weight: 500;
    }
  }
`;

export const Card = StyledCard as unknown as typeof $Card;
Card.Grid = $Card.Grid;
Card.Meta = $Card.Meta;

/* ---------- Breadcrumb ---------- */
const StyledBreadcrumb = styled($Breadcrumb)`
  &.ant-breadcrumb {
    .ant-breadcrumb-link {
      a:hover {
        background-color: transparent;
      }
    }
  }
`;

export const Breadcrumb = StyledBreadcrumb as unknown as typeof $Breadcrumb;
Breadcrumb.Item = $Breadcrumb.Item;
